import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import CreditCardForm from "./CreditCardForm";
import BankAccountForm from "./BankAccountForm";
import "../../assets/style/PayMethod.css";
import api from "../../api/api";

export default function PayMethodModal({ open, onClose, onPaymentMethodCreated }) {
  const [activeTab, setActiveTab] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  // Обновляем токен при изменении
  useEffect(() => {
    const token = sessionStorage.getItem("accessToken");
    setAssetsToken(token);
  }, []);

  // --- СОСТОЯНИЯ ДЛЯ ПОЛЕЙ ФОРМЫ ---
  // Для кредитной карты
  const [cardNumber, setCardNumber] = useState("");
  const [cardHolder, setCardHolder] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [cvv, setCvv] = useState("");

  const [assetsToken, setAssetsToken] = useState(
    sessionStorage.getItem("accessToken")
  );

  // Для банковского счета
  const [bankName, setBankName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");

  // Общее поле
  const [notes, setNotes] = useState("");

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Функция для очистки формы
  const clearForm = () => {
    setCardNumber("");
    setCardHolder("");
    setExpiryDate("");
    setCvv("");
    setBankName("");
    setAccountNumber("");
    setNotes("");
    setIsSubmitting(false);
  };

  // Функция для закрытия модального окна с очисткой формы
  const handleClose = () => {
    clearForm();
    onClose();
  };

  // --- ОБРАБОТЧИК ОТПРАВКИ ФОРМЫ ---
  const handleSubmit = async (event) => {
    event.preventDefault(); // Предотвращаем перезагрузку страницы
    setIsSubmitting(true);

    // Проверяем, есть ли токен
    const userToken = sessionStorage.getItem("accessToken");
    if (!userToken) {
      console.error("Токен не найден в sessionStorage");
      setNotification({
        show: true,
        message: 'Authentication required. Please log in again.',
        type: 'error'
      });
      setIsSubmitting(false);
      return;
    }

    if (activeTab === 0) {
      // Логика для отправки данных кредитной карты

      const reqBody = {
        type: "card",
        card_number: cardNumber,
        card_holder: cardHolder,
        card_expiration: expiryDate,
        cvv: cvv,
        notes: notes,
      };

      try {
        const response = await api.post(`/payment-methods`, reqBody, {
          headers: {
            Authorization: `Bearer ${userToken}`,
            "Content-Type": "application/json",
          },
        });

        console.log(response);
        
        // Вызываем callback для обновления списка
        if (onPaymentMethodCreated) {
          onPaymentMethodCreated();
        }
        
        handleClose(); // Закрываем модальное окно после отправки
      } catch (err) {
        console.log(err);
        if (err.response && err.response.status === 401) {
          setNotification({
            show: true,
            message: 'Session expired. Please log in again.',
            type: 'error'
          });
        } else {
          setNotification({
            show: true,
            message: 'Failed to add payment method. Please try again.',
            type: 'error'
          });
        }
        
        // Скрываем уведомление через 5 секунд
        setTimeout(() => {
          setNotification({ show: false, message: '', type: '' });
        }, 5000);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Логика для отправки данных банковского счета
      const bankAccountData = { bankName, accountNumber, notes };
      const reqBody = {
        type: "account",
        bank_name: bankName,
        account_number: accountNumber,
        card_expiration: expiryDate,
        notes: notes,
      };

      try {
        const response = await api.post(`/payment-methods`, reqBody, {
          headers: {
            Authorization: `Bearer ${userToken}`,
            "Content-Type": "application/json",
          },
        });

        console.log(response);
        
        // Вызываем callback для обновления списка
        if (onPaymentMethodCreated) {
          onPaymentMethodCreated();
        }
        
        handleClose(); // Закрываем модальное окно после отправки
      } catch (err) {
        console.log(err);
        if (err.response && err.response.status === 401) {
          setNotification({
            show: true,
            message: 'Session expired. Please log in again.',
            type: 'error'
          });
        } else {
          setNotification({
            show: true,
            message: 'Failed to add bank account. Please try again.',
            type: 'error'
          });
        }
        
        // Скрываем уведомление через 5 секунд
        setTimeout(() => {
          setNotification({ show: false, message: '', type: '' });
        }, 5000);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <>
      {/* Уведомление */}
      <Snackbar
        open={notification.show}
        autoHideDuration={5000}
        onClose={() => setNotification({ show: false, message: '', type: '' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification({ show: false, message: '', type: '' })}
          severity={notification.type}
          icon={notification.type === 'success' ? <CheckCircleIcon /> : <ErrorIcon />}
          sx={{
            backgroundColor: notification.type === 'success' ? '#22c55e' : '#ef4444',
            color: 'white',
            '& .MuiAlert-icon': {
              color: 'white'
            }
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={open}
        onClose={handleClose}
        classes={{ paper: "custom-dialog-paper" }}
        PaperProps={{
          elevation: 0,
        }}
      >
        {/* 👇 Оборачиваем все содержимое в тег form */}
        <form onSubmit={handleSubmit}>
          <DialogTitle className="dialog-title-container">
            Add Payment Method
            <IconButton onClick={handleClose} className="close-button">
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent className="dialog-content">
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              className="custom-tabs"
              variant="fullWidth"
            >
              <Tab label="Credit Card" className="custom-tab" />
              <Tab label="Bank Account" className="custom-tab" />
            </Tabs>

            {activeTab === 0 && (
              <CreditCardForm
                cardNumber={cardNumber}
                setCardNumber={setCardNumber}
                cardHolder={cardHolder}
                setCardHolder={setCardHolder}
                expiryDate={expiryDate}
                setExpiryDate={setExpiryDate}
                cvv={cvv}
                setCvv={setCvv}
                notes={notes}
                setNotes={setNotes}
              />
            )}
            {activeTab === 1 && (
              <BankAccountForm
                bankName={bankName}
                setBankName={setBankName}
                accountNumber={accountNumber}
                setAccountNumber={setAccountNumber}
                notes={notes}
                setNotes={setNotes}
              />
            )}

            <p className="security-note">
              Your data is encrypted and stored securely...
            </p>
          </DialogContent>

          <DialogActions className="dialog-actions-container">
            <Button
              type="submit"
              fullWidth
              variant="contained"
              className="action-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? <CircularProgress size={24} /> : "Add Payment Method"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}